import XLSX from 'xlsx';
import { filter, find } from 'ramda';
import { GameResource } from '~~apis/resource';
import { dateTimeFriendlyTransform, dateTimeStringPattern } from '~~utils/CommonUtils';
import Multilingual from '~~utils/Multilingual';
import { boxBattingColumns, boxPitchingColumns, boxFieldingColumns, initInGamePlayersOrder } from './Const';
import moment from 'moment';

export const updateGameMedia = (seasonUniqid, gameUniqid, { removedMediaList, addedMediaList }) => {
  return new Promise((resolve, reject) => {
    const appendMedia = (mediaListAfterRemoved) => {
      if (addedMediaList.length === 0) {
        resolve(mediaListAfterRemoved);
        return;
      }

      GameResource.appendMedia({
        seasonUniqid,
        gameUniqid,
        mediaList: addedMediaList.map((media) => {
          return {
            path: media.path,
            type: media.type,
          };
        }),
      })
        .then(({ data: { media: newMediaList } }) => {
          resolve(newMediaList);
        })
        .catch((e) => {
          reject(e);
        });
    };

    const removeImageList = filter((rm) => rm.type === 'PHOTO', removedMediaList);
    const removeVideoList = filter((rm) => rm.type === 'VIDEO', removedMediaList);

    const removeVideos = (newMediaListAfterImageRemove) => {
      if (removeVideoList.length > 0) {
        GameResource.removeMedia({
          seasonUniqid,
          gameUniqid,
          mediaPaths: removeVideoList.map((rm) => rm.path),
          mediaType: 'VIDEO',
        })
          .then(({ data: { media: newMediaList } }) => {
            appendMedia(newMediaList);
          })
          .catch((e) => {
            reject(e);
          });
      } else {
        appendMedia(newMediaListAfterImageRemove);
      }
    };
    const removeImages = () => {
      if (removeImageList.length > 0) {
        GameResource.removeMedia({
          seasonUniqid,
          gameUniqid,
          mediaPaths: removeImageList.map((rm) => rm.path),
          mediaType: 'PHOTO',
        })
          .then(({ data: { media: newMediaList } }) => {
            removeVideos(newMediaList);
          })
          .catch((e) => {
            reject(e);
          });
      } else {
        removeVideos([]);
      }
    };
    removeImages();
  });
};

export const updateGameNews = (seasonUniqid, gameUniqid, data) => {
  return new Promise((resolve, reject) => {
    GameResource.updateNews({ seasonUniqid, gameUniqid, data })
      .then(({ data: { news } }) => {
        resolve(news);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const deleteGameNews = (seasonUniqid, gameUniqid) => {
  return new Promise((resolve, reject) => {
    GameResource.deleteNews({ seasonUniqid, gameUniqid })
      .then(() => {
        resolve();
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const updateGameRelatedLinks = (seasonUniqid, gameUniqid, data) => {
  const validLinks = [];
  data.forEach((l) => {
    const title = l.title.trim();
    const link = l.link.trim();

    if (!link || !link.match(/^https:\/\//) || link.length > 2047) {
      return;
    }

    validLinks.push({ title, link });
  });

  return new Promise((resolve, reject) => {
    GameResource.updateRelatedLinks({ seasonUniqid, gameUniqid, data: validLinks })
      .then(({ data: updatedRelatedLinks }) => {
        resolve(updatedRelatedLinks);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const parseScoreBoardData = (game) => {
  return {
    uniqid: game.uniqid,
    seq: game.seq.toString(),
    group: game.group,
    away: {
      team: !game.away.team ? 'NONE' : game.away.team,
      scheduled_sp_uniqid: !game.away.scheduled_sp_uniqid ? '' : game.away.scheduled_sp_uniqid,
      scores: game.away.scores,
      scoresString: game.away.scores.join(','),
      runs: game.away.runs.toString(),
      hits: game.away.hits.toString(),
      record_battings: game.away.record_battings,
      record_pitchings: game.away.record_pitchings,
      record_fieldings: game.away.record_fieldings,
    },
    home: {
      team: !game.home.team ? 'NONE' : game.home.team,
      scheduled_sp_uniqid: !game.home.scheduled_sp_uniqid ? '' : game.home.scheduled_sp_uniqid,
      scores: game.home.scores,
      scoresString: game.home.scores.join(','),
      runs: game.home.runs.toString(),
      hits: game.home.hits.toString(),
      record_battings: game.home.record_battings,
      record_pitchings: game.home.record_pitchings,
      record_fieldings: game.home.record_fieldings,
    },
    info: {
      location_uniqid: !game.info.location_uniqid ? 'NONE' : game.info.location_uniqid,
      innings: game.info.innings.toString(),
      runs_without_innings: game.away.runs_without_innings, // 強迫分數跟局數搭配是兩隊同時開啟/關閉
      status: game.info.status,
      finished_status: game.info.finished_status,
      audience: game.info.audience || '',
      umpire: {
        umpireInChief: game.info.umpire?.umpireInChief || '',
        fbUmpire: game.info.umpire?.fbUmpire || '',
        sbUmpire: game.info.umpire?.sbUmpire || '',
        tbUmpire: game.info.umpire?.tbUmpire || '',
        lfUmpire: game.info.umpire?.lfUmpire || '',
        rfUmpire: game.info.umpire?.rfUmpire || '',
      },
      time: {
        ended_at: game.info.time.ended_at,
        scheduled_start_at: game.info.time.scheduled_start_at,
        started_at: game.info.time.started_at,
      },
    },
  };
};

export const validScoreBoardData = (autoGameSeq, games, originGameSeq, scoreBoardData) => {
  const errorInfo = {
    error: true,
    message: '',
    data: null,
  };

  let seq = 0;
  if (!autoGameSeq) {
    seq = parseInt(scoreBoardData.seq);
    if (Number.isNaN(seq) || seq <= 0) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_SEQ_FORMAT`);
      return errorInfo;
    }

    if (find((g) => g.seq === seq && g.group === scoreBoardData.group, games) && originGameSeq !== seq) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_SEQ_DUPLICATE`);
      return errorInfo;
    }
  }

  const dateTimeRegExp = new RegExp(`^${dateTimeStringPattern}$`);
  const scheduledStartAt = dateTimeFriendlyTransform(scoreBoardData.info.time.scheduled_start_at);
  if (!scheduledStartAt.match(dateTimeRegExp)) {
    errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_DATE_TIME_HINT`);
    return errorInfo;
  }

  let finishedStatus = '';
  let startedAt = '';
  let endedAt = '';
  let innings = 0;

  const getTeamInitState = () => {
    return {
      scores: [],
      runs: 0,
      hits: 0,
    };
  };
  const away = getTeamInitState();
  const home = getTeamInitState();

  if (scoreBoardData.info.status !== 'SCHEDULED') {
    if (scoreBoardData.away.team === 'NONE' || scoreBoardData.home.team === 'NONE') {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_NON_SCHEDULED_NO_TEAM`);
      return errorInfo;
    }

    if (scoreBoardData.info.location_uniqid === 'NONE') {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_NON_SCHEDULED_NO_LOCATION`);
      return errorInfo;
    }
  }
  if (scoreBoardData.info.status === 'PLAYING') {
    startedAt = dateTimeFriendlyTransform(scoreBoardData.info.time.started_at);
    if (!startedAt.match(dateTimeRegExp)) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_DATE_TIME_HINT`);
      return errorInfo;
    }
  }
  if (scoreBoardData.info.status === 'FINISHED') {
    finishedStatus = scoreBoardData.info.finished_status;
    startedAt = dateTimeFriendlyTransform(scoreBoardData.info.time.started_at);
    endedAt = dateTimeFriendlyTransform(scoreBoardData.info.time.ended_at);
    if (!finishedStatus) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_FINISHED_STATUS`);
      return errorInfo;
    }
    if (!startedAt.match(dateTimeRegExp)) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_DATE_TIME_HINT`);
      return errorInfo;
    }
    if (!endedAt.match(dateTimeRegExp)) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_DATE_TIME_HINT`);
      return errorInfo;
    }

    away.runs = parseInt(scoreBoardData.away.runs);
    if (Number.isNaN(away.runs) || away.runs < 0) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_SCORE_FORMAT`);
      return errorInfo;
    }
    home.runs = parseInt(scoreBoardData.home.runs);
    if (Number.isNaN(home.runs) || home.runs < 0) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_SCORE_FORMAT`);
      return errorInfo;
    }

    if (!scoreBoardData.info.runs_without_innings) {
      innings = parseInt(scoreBoardData.info.innings);
      if (Number.isNaN(innings) || innings < 1) {
        errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_INNING_FORMAT`);
        return errorInfo;
      }

      away.scores = scoreBoardData.away.scoresString.split(',');
      home.scores = scoreBoardData.home.scoresString.split(',');

      const validScores = (scores) => {
        if (scores.length !== innings) {
          return false;
        }

        const scoreRegExp = new RegExp(`^((\\d+x?)|-)$`);
        for (let score of scores) {
          if (score.match(scoreRegExp)) {
            continue;
          }

          return false;
        }
        return true;
      };
      if (!validScores(away.scores) || !validScores(home.scores)) {
        errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_TEAM_INNING_FORMAT`);
        return errorInfo;
      }

      away.hits = parseInt(scoreBoardData.away.hits);
      if (Number.isNaN(away.hits) || away.hits < 0) {
        errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_HITS_FORMAT`);
        return errorInfo;
      }
      home.hits = parseInt(scoreBoardData.home.hits);
      if (Number.isNaN(home.hits) || home.hits < 0) {
        errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_HITS_FORMAT`);
        return errorInfo;
      }
    }
  }

  return {
    error: false,
    message: '',
    data: {
      ...scoreBoardData,
      seq,
      away: {
        ...scoreBoardData.away,
        ...away,
      },
      home: {
        ...scoreBoardData.home,
        ...home,
      },
      info: {
        ...scoreBoardData.info,
        finished_status: finishedStatus,
        innings,
        time: {
          ...scoreBoardData.info.time,
          scheduled_start_at: scheduledStartAt,
          started_at: startedAt,
          ended_at: endedAt,
        },
      },
    },
  };
};

export const updateGameScoreBoard = (seasonUniqid, gameUniqid, data) => {
  return new Promise((resolve, reject) => {
    GameResource.updateScoreBoard({ seasonUniqid, gameUniqid, data })
      .then(({ data: newGame }) => {
        resolve(newGame);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const validStatsData = ({ records, batters, pitchers, inGameplayers }, originInGameplayers) => {
  const errorInfo = {
    error: true,
    message: '',
    data: null,
  };

  for (let p of [...records, ...batters, ...pitchers]) {
    if (!p.player.uniqid) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_SELECT`);
      return errorInfo;
    }
    continue;
  }
  for (let p of inGameplayers) {
    if (!p.info.uniqid) {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_SELECT`);
      return errorInfo;
    }
    continue;
  }

  for (let record of records) {
    if (record.player_type === '' || record.value === '') {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_SELECT`);
      return errorInfo;
    }

    record.comment = record.comment.trim();
    if (record.value !== 'SP_RECORD') {
      record.comment = '';
      continue;
    }

    if (record.comment === '') {
      errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_SP_RECORD_EMPTY_COMMENT`);
      return errorInfo;
    }
  }

  for (let batter of batters) {
    for (let column of boxBattingColumns) {
      switch (column) {
        case 'player':
        // fallthrough
        case 'is_PH':
          continue;
        case 'order':
          if (Number.isNaN(parseInt(batter.order)) || parseInt(batter.order) < 1) {
            errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_BATTER_ORDER`);
            return errorInfo;
          }
        // fallthrough
        default:
          batter[column] = Number.isNaN(parseInt(batter[column])) ? 0 : parseInt(batter[column]);
          if (batter[column] < 0) {
            errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_BATTER_COLUMN_INT`);
            return errorInfo;
          }
          break;
      }
    }
  }

  for (let pitcher of pitchers) {
    for (let column of boxPitchingColumns) {
      switch (column) {
        case 'player':
          continue;
        default:
          pitcher[column] = Number.isNaN(parseInt(pitcher[column])) ? 0 : parseInt(pitcher[column]);
          if (pitcher[column] < 0) {
            errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_PITCHER_COLUMN_INT`);
            return errorInfo;
          }
          break;
      }
    }
  }

  for (let player of inGameplayers) {
    const originPlayer = find((op) => op.info.uniqid === player.info.uniqid, originInGameplayers);
    const positionAmount = {};

    for (let fielding of player.fieldings) {
      for (let column of boxFieldingColumns) {
        switch (column) {
          case 'player':
            continue;
          case 'position':
            if (!fielding.position) {
              errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_SELECT`);
              return errorInfo;
            }
            continue;
          default:
            if (['DH', 'PH', 'PR'].includes(fielding.position) && column !== 'order') {
              fielding[column] = 0;
              break;
            }
            if (fielding.position !== 'C' && ['PB', 'SB', 'CS'].includes(column)) {
              fielding[column] = 0;
              break;
            }

            fielding[column] = Number.isNaN(parseInt(fielding[column])) ? 0 : parseInt(fielding[column]);
            if (fielding[column] < 0) {
              errorInfo.message = Multilingual(`TOURNAMENT.ERROR.GAME_STATS_FIELDING_COLUMN_INT`);
              return errorInfo;
            }
            break;
        }
      }

      if (!positionAmount[fielding.position]) {
        positionAmount[fielding.position] = 0;
      }
      positionAmount[fielding.position]++;

      fielding.in_play_ball_counts = 0;
      // 由於 PbP 的進場時間不會在 Box 呈現，這邊如果有找到 PbP 的資料就附進來，沒有也沒辦法了
      if (!originPlayer) {
        continue;
      }

      // 同一個位置可能上去很多遍，要抓進場球數就要抓同個位置同一次進場
      let samePositionCount = 0;
      for (let originFielding of originPlayer.fieldings) {
        if (originFielding.position !== fielding.position) {
          continue;
        }

        samePositionCount++;
        if (samePositionCount !== positionAmount[fielding.position]) {
          continue;
        }
        fielding.in_play_ball_counts = originFielding.in_play_ball_counts;
        break;
      }
    }
  }

  return {
    error: false,
    message: '',
    data: { records, batters, pitchers, players: inGameplayers },
  };
};

export const updateGameStats = (seasonUniqid, gameUniqid, data) => {
  return new Promise((resolve, reject) => {
    GameResource.updateStats({ seasonUniqid, gameUniqid, data })
      .then(({ data }) => {
        resolve({
          ...data,
          away: {
            ...data.away,
            players: initInGamePlayersOrder(data.away.players, data.away.batters, data.away.pitchers),
          },
          home: {
            ...data.home,
            players: initInGamePlayersOrder(data.home.players, data.home.batters, data.home.pitchers),
          },
        });
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const playerInitialState = {
  action: '',
  player_uniqid: '',
  name: '',
  number: '',
  avatar_url: '',
  avatar_path: '',
  birthday: '',
  first_position: '',
  second_position: '',
  p_hand: '',
  b_hand: '',
  player_cross_league: false,
  newName: '',
  newNumber: '',
  errorMsg: '',
  possiblePlayers: [],
  playerStatus: '',
};
export const playerReducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { ...playerInitialState };
    case 'start':
      return { ...playerInitialState, ...action.payload };
    case 'updateColumn':
      return { ...state, ...action.payload, errorMsg: '' };
    case 'removeAvatar':
      return { ...state, avatar_path: '', avatar_url: '', errorMsg: '' };
    case 'error':
      return { ...state, playerStatus: 'ERROR', errorMsg: action.payload };
    case 'playerStatus':
      return { ...state, playerStatus: action.payload };
    case 'possibilityCheck':
      return { ...state, playerStatus: 'PLAYER_CHOICE', possiblePlayers: action.payload };
    default:
      throw new Error();
  }
};

export const updateGamePbP = (seasonUniqid, gameUniqid, data) => {
  return new Promise((resolve, reject) => {
    GameResource.updatePbP({ seasonUniqid, gameUniqid, data })
      .then(({ data }) => {
        resolve({
          ...data,
          away: {
            ...data.away,
            players: initInGamePlayersOrder(data.away.players, data.away.batters, data.away.pitchers),
          },
          home: {
            ...data.home,
            players: initInGamePlayersOrder(data.home.players, data.home.batters, data.home.pitchers),
          },
        });
      })
      .catch((e) => {
        reject(e);
      });
  });
};

export const exportGameBoxScoreExcel = (game, batch = false) => {
  const getGameInfoRows = () => {
    let statusWording = Multilingual(`GAME.STATUS.${game.info.status}`);
    if (game.info.status === 'FINISHED') {
      statusWording += `（${Multilingual(`GAME.FINISHED_STATUS.${game.info.finished_status}`)}）`;
    }

    const startedAtWording = !game.info.time.started_at
      ? ''
      : moment(game.info.time.started_at).format('YYYY/MM/DD HH:mm');
    const endedAtWording = !game.info.time.ended_at ? '' : moment(game.info.time.ended_at).format('YYYY/MM/DD HH:mm');

    const exportRows = [
      [Multilingual(`GAME.STATUS.WORDING`), '', Multilingual(`GAME.LOCATION`), game.info.location, '', ''],
      [statusWording, '', Multilingual(`GAME.STARTED_AT`), startedAtWording, '', game.away.team],
      ['', '', Multilingual(`GAME.ENDED_AT`), endedAtWording, '', game.home.team],
      [],
    ];

    const headerRowIndex = 0;
    const awayRowIndex = 1;
    const homeRowIndex = 2;
    if (!game.away.runs_without_innings && !game.home.runs_without_innings) {
      const maxInningLength = Math.max(game.away.scores.length, game.home.scores.length);
      for (let inningIndex = 0; inningIndex < maxInningLength; inningIndex++) {
        exportRows[headerRowIndex].push({ t: 's', v: inningIndex + 1 });
        exportRows[awayRowIndex].push({
          t: 's',
          v: !game.away.scores[inningIndex] ? '-' : game.away.scores[inningIndex],
        });
        exportRows[homeRowIndex].push({
          t: 's',
          v: !game.home.scores[inningIndex] ? '-' : game.home.scores[inningIndex],
        });
      }
    }

    exportRows[headerRowIndex].push({ t: 's', v: 'R' });
    exportRows[awayRowIndex].push({ t: 's', v: game.away.runs });
    exportRows[homeRowIndex].push({ t: 's', v: game.home.runs });

    exportRows[headerRowIndex].push({ t: 's', v: 'H' });
    exportRows[awayRowIndex].push({ t: 's', v: game.away.hits });
    exportRows[homeRowIndex].push({ t: 's', v: game.home.hits });

    return exportRows;
  };

  const getInitRows = () => [
    [
      Multilingual(`GAME.STATS.BATTING.WORDING`), // 給背號用
      ...boxBattingColumns.map((battingColumn) => Multilingual(`STATS.BATTER.${battingColumn.toUpperCase()}`)),
      '',
      Multilingual(`GAME.STATS.PITCHING.WORDING`), // 給背號用
      ...boxPitchingColumns.map((pitchingColumn) => Multilingual(`STATS.PITCHER.${pitchingColumn.toUpperCase()}`)),
      '',
      Multilingual(`GAME.STATS.FIELDING.WORDING`), // 給背號用
      ...boxFieldingColumns.map((fieldingColumn) => Multilingual(`STATS.FIELDER.${fieldingColumn.toUpperCase()}`)),
      '',
      Multilingual(`GAME.STATS.RECORD.WORDING`), // 給背號用
      Multilingual(`GAME.STATS.RECORD.PLAYER`),
      Multilingual(`GAME.STATS.RECORD.TYPE.WORDING`),
      Multilingual(`GAME.STATS.RECORD.VALUE.WORDING`),
    ],
  ];

  const getRows = (oneSide) => {
    const exportRows = getInitRows();

    const flatFielding = [];
    oneSide.players.forEach((fielder) => {
      fielder.fieldings.forEach((fielding) => {
        flatFielding.push({
          player: fielder.info,
          ...fielding,
        });
      });
    });

    const maxLength = Math.max(
      oneSide.batters.length,
      oneSide.pitchers.length,
      flatFielding.length,
      oneSide.records.length,
    );
    for (let i = 0; i < maxLength; i++) {
      const batter = oneSide.batters[i];
      const pitcher = oneSide.pitchers[i];
      const fielding = flatFielding[i];
      const record = oneSide.records[i];

      const row = [
        !batter ? '' : { t: 's', v: `#${batter.player.number}` },
        ...boxBattingColumns.map((battingColumn) => {
          if (!batter) {
            return '';
          }
          switch (battingColumn) {
            case 'player':
              return { t: 's', v: batter.player.name };
            case 'is_PH':
              return { t: 's', v: batter.is_PH ? 'V' : '' };
            default:
              return batter[battingColumn];
          }
        }),
        '',
        !pitcher ? '' : { t: 's', v: `#${pitcher.player.number}` },
        ...boxPitchingColumns.map((pitchingColumn) => {
          if (!pitcher) {
            return '';
          }
          switch (pitchingColumn) {
            case 'player':
              return { t: 's', v: pitcher.player.name };
            default:
              return pitcher[pitchingColumn];
          }
        }),
        '',
        !fielding ? '' : { t: 's', v: `#${fielding.player.number}` },
        ...boxFieldingColumns.map((fieldingColumn) => {
          if (!fielding) {
            return '';
          }
          switch (fieldingColumn) {
            case 'player':
              return { t: 's', v: fielding.player.name };
            case 'PB':
            case 'SB':
            case 'CS':
              if (fielding.position !== 'C') {
                return '';
              }
              return { t: 's', v: fielding[fieldingColumn] };
            default:
              return fielding[fieldingColumn];
          }
        }),
      ];
      if (record) {
        row.push('');
        row.push({ t: 's', v: `#${record.player.number}` });
        row.push({ t: 's', v: record.player.name });
        row.push({ t: 's', v: Multilingual(`GAME.STATS.RECORD.TYPE.${record.player_type}`) });
        row.push({ t: 's', v: Multilingual(`GAME.STATS.RECORD.VALUE.${record.value}`) });
        row.push({ t: 's', v: record.comment });
      }
      exportRows.push(row);
    }

    return exportRows;
  };

  const awayRows = getRows(game.away);
  const homeRows = getRows(game.home);

  const gameInfoRows = getGameInfoRows();

  if (batch) {
    return {
      away: awayRows,
      home: [...gameInfoRows, ...homeRows],
    };
  }

  const title = `G${game.seq}BoxScore成績`;
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: title,
    Author: 'Rebas 野革',
    CreatedDate: new Date(),
  };
  wb.SheetNames.push('AWAY');
  wb.SheetNames.push('HOME');

  const awaySheet = XLSX.utils.aoa_to_sheet(awayRows);
  wb.Sheets['AWAY'] = awaySheet;
  const homeSheet = XLSX.utils.aoa_to_sheet([...gameInfoRows, ...homeRows]);
  wb.Sheets['HOME'] = homeSheet;

  XLSX.writeFile(wb, `${title}.xlsx`);
};

export const exportTeamPbP = (team, games) => {
  const title = `${team.name}逐打席資料`;
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: title,
    Author: 'Rebas 野革',
    CreatedDate: new Date(),
  };
  wb.SheetNames.push('逐打席');

  const exportRows = [
    [
      'opponent',
      'side',
      'innings',
      'away_scores',
      'home_scores',
      'started_at',
      '',
      'inning',
      'inning_frame',
      'is_opponent_PA',
      'pitcher_name',
      'pitcher_number',
      'pitcher_hand',
      'catcher_name',
      'catcher_number',
      'batter_name',
      'batter_number',
      'batter_hand',
      'PA_round',
      'PA_order',
      'pinch',
      'away_score',
      'home_score',
      'start_outs',
      'outs',
      'bases',
      'ball_counts',
      'drop_point',
      'trajectory',
      'RBI',
      'result',
      'R',
      'R_pitcher_name',
      'R_pitcher_number',
      'is_ER',
      'SB',
      'CS',
      'PO',
    ],
  ];

  const getPCPerBall = (fielders) => {
    const pitcherPerBall = [];
    const catcherPerBall = [];
    fielders.forEach((fielder) => {
      if (fielder.position !== 1 && fielder.position !== 2) {
        return;
      }

      const targetPerBall = fielder.position === 1 ? pitcherPerBall : catcherPerBall;

      let lastTarget = targetPerBall[0];
      if (targetPerBall.length === 0) {
        lastTarget = fielder.player;
      }

      for (let i = 0; i < fielder.in_play_ball_counts; i++) {
        if (targetPerBall[i]) {
          continue;
        }
        targetPerBall[i] = lastTarget;
      }

      targetPerBall.push(fielder.player);
    });

    return { pitcherPerBall, catcherPerBall };
  };

  const appendRows = (gameInfo, isOpponent, pbp, opponentCatcherPerBall) => {
    let ballCount = 0;
    pbp.forEach((PA) => {
      let catcherName = '';
      let catcherNumber = '';
      if (opponentCatcherPerBall.length > 0) {
        let catcher = opponentCatcherPerBall[ballCount];
        if (!catcher) {
          catcher = opponentCatcherPerBall[opponentCatcherPerBall.length - 1];
        }

        catcherName = catcher.name;
        catcherNumber = catcher.number;
      }

      exportRows.push([
        ...gameInfo,
        '',
        PA.inning,
        PA.inning_frame,
        isOpponent,
        PA.pitcher.name,
        { t: 's', v: PA.pitcher.number },
        PA.p_hand,
        catcherName,
        { t: 's', v: catcherNumber },
        PA.batter.name,
        { t: 's', v: PA.batter.number },
        PA.b_hand,
        PA.PA_round,
        PA.PA_order,
        PA.pinch,
        PA.away_score,
        PA.home_score,
        PA.start_outs,
        PA.outs,
        PA.bases,
        { t: 's', v: PA.ball_counts },
        { t: 's', v: PA.drop_point },
        { t: 's', v: PA.trajectory },
        PA.RBI,
        PA.result,
        PA.R,
        PA.R === 0 ? '' : PA.R_count_on_pitcher ? PA.R_count_on_pitcher.name : PA.pitcher.name,
        { t: 's', v: PA.R === 0 ? '' : PA.R_count_on_pitcher ? PA.R_count_on_pitcher.number : PA.pitcher.number },
        PA.is_ER,
        { t: 's', v: PA.SB },
        PA.CS,
        PA.PO,
      ]);

      ballCount += PA.ball_counts.length;
    });
  };

  const appendGameSheet = (
    game,
    pbp,
    pitcherPerBall,
    catcherPerBall,
    opponentPbP,
    opponentPitcherPerBall,
    opponentCatcherPerBall,
  ) => {
    const perBallRows = [
      [
        'inning',
        'inning_frame',
        'pitcher_name',
        'pitcher_number',
        'pitcher_hand',
        'catcher_name',
        'catcher_number',
        'batter_name',
        'batter_number',
        'batter_hand',
        'PA_round',
        'PA_order',
        'ball_count',
        'ball_result',
        '',
        '',
        '',
        '',
        'inning',
        'inning_frame',
        'pitcher_name',
        'pitcher_number',
        'pitcher_hand',
        'catcher_name',
        'catcher_number',
        'batter_name',
        'batter_number',
        'batter_hand',
        'PA_round',
        'PA_order',
        'ball_count',
        'ball_result',
        '',
        '',
        '',
      ],
    ];

    const genRows = (targetPbP, targetOpponentPitcherPerBall, targetOpponentCatcherPerBall) => {
      const rows = [];

      let ballCount = 0;
      targetPbP.forEach((PA) => {
        let currentStrike = 0;
        let currentBall = 0;

        let currentPitcherUniqid = '';
        let currentCatcherUniqid = '';
        PA.ball_counts.forEach((ballResult, ballSeq) => {
          let pitcherName = PA.pitcher.name;
          let pitcherNumber = { t: 's', v: PA.pitcher.number };
          let pitcherHand = PA.p_hand;
          let pitcherUniqid = PA.pitcher.uniqid;
          if (targetOpponentPitcherPerBall.length > 0) {
            let pitcher = targetOpponentPitcherPerBall[ballCount];
            if (!pitcher) {
              pitcher = targetOpponentPitcherPerBall[targetOpponentPitcherPerBall.length - 1];
            }

            pitcherName = pitcher.name;
            pitcherNumber = { t: 's', v: pitcher.number };
            pitcherHand = '';
            pitcherUniqid = pitcher.uniqid;
          }

          let catcherName = '';
          let catcherNumber = '';
          let catcherUniqid = '';
          if (targetOpponentCatcherPerBall.length > 0) {
            let catcher = targetOpponentCatcherPerBall[ballCount];
            if (!catcher) {
              catcher = targetOpponentCatcherPerBall[targetOpponentCatcherPerBall.length - 1];
            }

            catcherName = catcher.name;
            catcherNumber = { t: 's', v: catcher.number };
            catcherUniqid = catcher.uniqid;
          }

          if (currentPitcherUniqid === pitcherUniqid) {
            pitcherName = '';
            pitcherNumber = '';
            pitcherHand = '';
          }
          if (currentCatcherUniqid === catcherUniqid) {
            catcherName = '';
            catcherNumber = '';
          }

          const row = [
            '',
            '',
            pitcherName,
            pitcherNumber,
            pitcherHand,
            catcherName,
            catcherNumber,
            '',
            '',
            '',
            '',
            '',
            `${currentBall}-${currentStrike}`,
          ];
          currentPitcherUniqid = pitcherUniqid;
          currentCatcherUniqid = catcherUniqid;

          let ballResultWording = '';
          // 該打席最後一顆球（而且是結束在打者身上）
          if (ballSeq === PA.ball_counts.length - 1 && PA.result) {
            switch (ballResult) {
              case 'S':
                ballResultWording = '未揮棒';
                break;
              case 'SW':
                ballResultWording = '揮棒';
                break;
              case 'B':
                ballResultWording = '壞球';
                break;
              case 'F':
                ballResultWording = '擦棒';
                break;
              case 'H':
                ballResultWording = '打擊出去';
                break;
              default:
                break;
            }
            ballResultWording += `-${PA.result}`;
          } else {
            switch (ballResult) {
              case 'S':
                ballResultWording = '未揮棒好球';
                break;
              case 'SW':
                ballResultWording = '揮棒好球';
                break;
              case 'B':
                ballResultWording = '壞球';
                break;
              case 'F':
                ballResultWording = '界外球';
                break;
              default:
                break;
            }
          }
          row.push(ballResultWording);

          if (ballSeq === 0) {
            row[0] = PA.inning;
            row[1] = PA.inning_frame;
            row[7] = PA.batter.name;
            row[8] = { t: 's', v: PA.batter.number };
            row[9] = PA.b_hand;
            row[10] = PA.PA_round;
            row[11] = PA.PA_order;
          }
          ballCount++;
          if (ballResult === 'B') {
            currentBall++;
          } else if (currentStrike < 2) {
            currentStrike++;
          }
          rows.push(row);
        });
      });

      return rows;
    };

    const rows = genRows(pbp, opponentPitcherPerBall, opponentCatcherPerBall);
    const opponentRows = genRows(opponentPbP, pitcherPerBall, catcherPerBall);
    let rowMaxIndex = Math.max(rows.length, opponentRows.length) - 1;
    for (let rowIndex = 0; rowIndex <= rowMaxIndex; rowIndex++) {
      const leftSideRow = rows[rowIndex] ? rows[rowIndex] : new Array(14).fill('');
      const row = opponentRows[rowIndex]
        ? [...leftSideRow, ...new Array(4).fill(''), ...opponentRows[rowIndex]]
        : leftSideRow;
      perBallRows.push(row);
    }

    const sheetName = `${moment(game.started_at).format('YYYYMMDD_HHmm')}_vs_${game.opponent_team_name}`;
    const gameSheet = XLSX.utils.aoa_to_sheet(perBallRows);
    wb.SheetNames.push(sheetName);
    wb.Sheets[sheetName] = gameSheet;
  };

  games.forEach((game) => {
    const gameInfo = [
      game.opponent_team_name,
      game.info.side,
      game.info.innings,
      { t: 's', v: game.info.away_scores },
      { t: 's', v: game.info.home_scores },
      game.started_at,
    ];

    const { pitcherPerBall, catcherPerBall } = getPCPerBall(game.fielders);
    const { pitcherPerBall: opponentPitcherPerBall, catcherPerBall: opponentCatcherPerBall } = getPCPerBall(
      game.opponent_fielders,
    );
    appendRows(gameInfo, false, game.pbp, opponentCatcherPerBall);
    appendRows(gameInfo, true, game.opponent_pbp, catcherPerBall);
    appendGameSheet(
      game,
      game.pbp,
      pitcherPerBall,
      catcherPerBall,
      game.opponent_pbp,
      opponentPitcherPerBall,
      opponentCatcherPerBall,
    );
  });

  const sheet = XLSX.utils.aoa_to_sheet(exportRows);
  wb.Sheets['逐打席'] = sheet;

  XLSX.writeFile(wb, `${title}.xlsx`);
};
